import {DefaultArcObject, Selection} from 'd3';
import * as d3 from 'd3';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {LINK_HIGHLIGHT_CIRCULAR_STROKE_COLOR} from 'molbio/maps/BiointelligentLinksConstants';
import {BiointelligentLinkHighlightLayout} from 'molbio/maps/circular/CircularLayout';
import LabeledSegmentsRingView from 'molbio/maps/circular/LabeledSegmentsRingView';
import {
  getBiointelligentLinkRegionsForSlice,
  ViewBiointelligentLink,
} from 'molbio/maps/translateLinksToHighlightRegions';

/*
 * Note that there is some dangerous typing going on here. All other circular views extending from
 * SegmentsRingView use a data type that extends RegionSelectable appropriately and are therefore safe
 * because they conform to the interface requirements of the MapsUtil helper functions invoked by
 * SegmentsRingView (namely getMax and getLayers).
 *
 * BiointelligentLinkHighlightView breaks this pattern by using ViewBiointelligentLink as the data model so it doesn't meet the interface
 * requirements of some SegmentsRingView method. In practice, this does not raise errors due to the
 * redefinition of updateLayers and likely some other logic.
 */
// @ts-expect-error
class BiointelligentLinkHighlightView extends LabeledSegmentsRingView<
  BiointelligentLinkHighlightLayout,
  // @ts-expect-error
  ViewBiointelligentLink
> {
  static initClass(): void {
    this.prototype.className = 'biointelligent-link-highlight-label';
    this.prototype.singularClassName = 'biointelligent-link-highlights-label';
    this.prototype.tagName = 'g';

    // Need to set contextMenuData to null to default to showing the parts context menu.
    // Should revisit this behavior to match other features.
    // @ts-expect-error
    this.prototype.contextMenuData = null;
  }

  override shouldCaptureLeftClick(): boolean {
    return false;
  }

  override shouldCaptureDoubleClick(): boolean {
    return true;
  }

  override contextMenuOptions(d: ViewBiointelligentLink): MenuChoicesOptions {
    return {tabstate: this.tabstate};
  }

  override arc(d: ViewBiointelligentLink): d3.Arc<unknown, DefaultArcObject> {
    return d3.arc();
  }

  override data(): Array<ViewBiointelligentLink> {
    const start = this.model.get('leftBP');
    const end = this.model.get('rightBP');
    const sequence = this.model.get('sequence');

    const supportedLinkTypes = [BiointelligentLinkType.DNA_PART, BiointelligentLinkType.RNA_PART];
    if (
      BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS &&
      BenchlingConfig.MOLBIO_ENABLE_EDITING_BIOINTELLIGENT_LINKS
    ) {
      supportedLinkTypes.push(BiointelligentLinkType.TRANSCRIPTION);
    }
    const regions = getBiointelligentLinkRegionsForSlice(start, end, sequence, supportedLinkTypes);
    return regions.map((region) => {
      const links = region.links;
      return new ViewBiointelligentLink({
        start: region.start,
        end: region.end,
        links: region.links,
        highlightedStrand: region.highlightedStrand,
        // When PARTS_ENABLE_OVERLAPPING_PARTS is disabled, the name is used by handleText.
        // When that is the case, links.length should always be 1 - there will not be any cases
        // of transcriptions overlapping with DNA or RNA parts because we only show transcriptions
        // when PARTS_ENABLE_OVERLAPPING_PARTS is enabled.
        name: links.length === 1 ? links[0].name : '',
        isArchived: links.some((l) => l.isArchived),
        outOfSync: links.some((l) => l.outOfSync),
        partRefSequenceId: null,
      });
    });
  }

  override stroke(d: ViewBiointelligentLink): string {
    return LINK_HIGHLIGHT_CIRCULAR_STROKE_COLOR;
  }

  override fill(d: ViewBiointelligentLink): string {
    return d.color();
  }

  override baseLine(layer: number): number {
    return this.radius(layer + 1); // + 1 renders on top.
  }

  override opacity(d: ViewBiointelligentLink): number {
    return 0.4;
  }

  override registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:bases', () => {
      this.invalidateData();
    });
  }

  override updateRadius(): void {
    this.radius.range([this.getInnerRadius() - 5, this.layout.get('DNA')!.get('outerRadius') + 5]);
  }

  override updateLayers(data: Array<ViewBiointelligentLink>): void {
    // eslint-disable-next-line no-restricted-syntax
    _.each(data, (datum) => datum.set({annularLayer: 0}));
    this.layout.set('outerRadius', this.getInnerRadius());
  }

  override handleText(selector: Selection<SVGGElement, ViewBiointelligentLink, SVGGElement, unknown>): void {
    if (!BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      super.handleText(selector);
    }
  }

  // @ts-expect-error We helpfully do something custom with the part overlay here instead of making it a
  // Selectable
  clickAction(e: Event, d: ViewBiointelligentLink): void {}

  // @ts-expect-error We helpfully do something custom with the part overlay here instead of making it a
  // Selectable
  dblClickAction(e: Event, d: ViewBiointelligentLink): void {
    // The sequence double click handler already contains the logic for selecting the correct part regions.
    // Default to selecting the whole sequence when the region is an overlap, which is the same behavior
    // as double clicking on a location with no parts.
    this.model.get('sequence').handleDoubleClick(0, this.model.get('sequence').length());
  }
}
BiointelligentLinkHighlightView.initClass();

export default BiointelligentLinkHighlightView;
